import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import ShoppingCartIcon from '@/components/icons/shopping-cart-icon';

import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import useModal from '@/hooks/useModal';
import { deleteCartItem, retrieveCart, updateCartItem } from '@/lib/actions/cart/action';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import { cn, debounce, formatIDR, formatNumber } from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { BaseCart } from '@/types/http/cart/common';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, Loader2, MinusIcon, PlusIcon, Trash2, XIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';

export interface CartDrawerProps {
  openModal?: boolean;
  cart: HttpTypes.StoreCart;
  setOpenModal?: (open: boolean) => void;
  host?: string;
  theme: ThemeType;
}

export function CartDrawer({ openModal, setOpenModal, cart, host, theme }: CartDrawerProps) {
  const { multiOrigin } = useCookies();
  const router = useRouter();
  const pathname = usePathname();
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';
  const isPreview = query.get('isPreview') || false;

  const [isLoading, setIsLoading] = React.useState<{ [key: string]: boolean }>({});
  const [currentItemQty, setCurrentItemQty] = React.useState<{ [key: string]: number }>({});
  const [subtotal, setSubtotal] = React.useState<number>(0);

  const cartItems = cart?.items;
  const isEmptyCart = cartItems?.length === 0;

  React.useEffect(() => {
    const newSubtotal = cartItems?.reduce((total, item) => {
      const qty = currentItemQty[item.id] || item.quantity;
      return total + (item.discount_price ? item.discount_price : item.sell_price) * qty;
    }, 0);
    setSubtotal(newSubtotal || 0);
  }, [currentItemQty, cartItems]);

  const debouncedIncrementQty = React.useCallback(
    debounce(async (item: StoreCart, qty: number) => {
      setIsLoading((prev) => ({ ...prev, [item.id]: true }));
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }, 500),
    []
  );

  const debouncedDecrementQty = React.useCallback(
    debounce(async (item: StoreCart, qty: number) => {
      setIsLoading((prev) => ({ ...prev, [item.id]: true }));
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }, 500),
    []
  );

  const handleIncremenQty = (item: StoreCart, idx: number = 0) => {
    const newQty = (currentItemQty[item.id] || item.quantity) + 1;
    if (newQty > (cart?.item_stock?.[idx]?.stock || 0)) return;
    setCurrentItemQty((prev) => ({ ...prev, [item.id]: newQty }));
    debouncedIncrementQty(item, newQty);
  };

  const handleDecrementQty = (item: StoreCart) => {
    const newQty = Math.max(1, (currentItemQty[item.id] || item.quantity) - 1);
    setCurrentItemQty((prev) => ({ ...prev, [item.id]: newQty }));
    debouncedDecrementQty(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    if (isPreview || !!token) return;
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));
    await deleteCartItem(host as string, item.id);
    queryClient.invalidateQueries({ queryKey: ['carts'] });
    SendEventAnalytic('remove_from_cart', {
      currency: 'IDR',
      value: Number(item.sell_price) * item.quantity,
      items: [
        {
          item_name: item?.product_name,
          item_id: item.id as string,
          price: Number(item?.sell_price),
          quantity: item.quantity,
          unique_action_id: Date.now(),
        },
      ],
    });
    SendEventMetapixel('RemoveFromCart', {
      content_ids: [item.id],
      content_name: item.product_name,
      content_type: 'product',
      quantity: item.quantity,
      value: Number(item.sell_price) * item.quantity,
      currency: 'IDR',
    });
    setIsLoading((prev) => ({ ...prev, [item.id]: false }));
  };

  const handleToCart = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, ['checkout']: true }));

      if (isPreview || !!token) return;

      const result = await updateCartCheckout(host as string);

      if (result.success) {
        return router.push(`/checkout/${result.data.checkout_id}`);
      }

      if (!cartItems?.length) {
        return toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setOpenModal?.(false);
      setIsLoading((prev) => ({ ...prev, ['checkout']: false }));
    }
  };

  const handleOutOfStock = (idx: number) => {
    return cart?.item_stock?.[idx]?.stock === 0;
  };

  const checkStockAfterChangeOrigin = (idx: number, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };
  const handleDisableAction = (idx: number, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };

  const disableButton = cart?.item_stock?.some((item) => item.stock === 0);

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='right'>
      <DrawerContent className='theme_cart_drawer_bg theme_font theme_drawer fixed inset-y-0 right-0 z-[106] flex h-auto w-full max-w-[420px] flex-col border'>
        {isEmptyCart ? (
          <>
            <DrawerHeader className='flex items-center justify-between text-left'>
              <DrawerTitle />
              <DrawerClose>
                <XIcon className='theme_cart_drawer_color h-4 w-4' />
              </DrawerClose>
            </DrawerHeader>
            <div className='flex h-full items-center justify-center px-4'>
              <div className='theme_cart_drawer_color text-xl'>Your cart is empty</div>
            </div>
          </>
        ) : (
          <>
            <DrawerHeader className='flex items-center justify-between text-left'>
              <DrawerTitle className='theme_cart_drawer_color'>Your Cart</DrawerTitle>
              <DrawerClose>
                <XIcon className='h-4 w-4' />
              </DrawerClose>
            </DrawerHeader>
            <div className='px-4'>
              <div className='theme_cart_drawer_color grid grid-cols-6 border-b py-2 text-xs uppercase'>
                <div className='col-span-4'>Product</div>
                <div className='col-span-2 text-right'>Total</div>
              </div>
            </div>
            <ScrollArea className='h-full overflow-y-auto px-4' type='scroll'>
              {cartItems?.map((item, idx) => {
                const images = (item.product_assets || []).filter(
                  (asset) => asset.assets_type === 'image'
                );
                const displayedQuantity = currentItemQty[item.id] || item.quantity;
                const totalPrice =
                  displayedQuantity * (item.discount_price ? item.discount_price : item.sell_price);
                const isOutOfStock = handleOutOfStock(idx || 0);
                const disable = handleDisableAction(idx || 0, item.quantity);
                const showNotifyStock = checkStockAfterChangeOrigin(idx || 0, item.quantity);
                const stock = Number(cart?.item_stock?.[idx]?.stock);
                const suggestionStock = (idx: number) => {
                  return cart?.item_stock?.[idx]?.available_stock || [];
                };

                return (
                  <div className='theme_cart_drawer_color flex gap-x-2 border-b py-4' key={item.id}>
                    <div className='mt-1 h-24 w-24'>
                      <Image
                        src={images[0]?.assets_url}
                        alt={item.product_name}
                        width={80}
                        height={80}
                        className={cn(
                          'aspect-square w-36 border object-contain',
                          isOutOfStock && 'grayscale'
                        )}
                      />
                    </div>
                    <div className='flex flex-1 flex-col gap-y-2'>
                      <div className={cn('w-fit', isOutOfStock && 'grayscale')}>
                        <BadgeStoreFront theme={theme} item={item} />
                      </div>

                      <div className='grid grid-cols-6'>
                        <div className='col-span-4'>
                          <div className='flex gap-x-3'>
                            <div className='w-full space-y-1'>
                              <h2
                                className={cn(
                                  'theme_cart_page_color line-clamp-3 text-sm',
                                  isOutOfStock && 'text-gray-500'
                                )}
                              >
                                {item.product_name}
                              </h2>
                              {item.variants?.map((variant) => (
                                <p
                                  key={variant.option_id}
                                  className={cn('text-gray-500', isOutOfStock && 'text-gray-500')}
                                >
                                  {variant.label}
                                </p>
                              ))}
                              <div
                                className={cn(
                                  item?.discount_price && 'flex flex-col items-start gap-2'
                                )}
                              >
                                <span
                                  className={cn(
                                    ' text-base font-bold',
                                    isOutOfStock ? '!text-gray-500' : 'text_product_secondary'
                                  )}
                                >
                                  {formatIDR(
                                    item?.discount_price
                                      ? item?.discount_price
                                      : item?.sell_price || 0
                                  )}
                                </span>
                                {(item?.discount_price as number) > 0 ? (
                                  <span
                                    className={cn(
                                      'text-sm text-gray-500 line-through',
                                      isOutOfStock && 'text-gray-500'
                                    )}
                                  >
                                    {formatIDR(item?.sell_price || 0)}
                                  </span>
                                ) : null}
                              </div>
                              <div className='flex gap-x-2 pt-2'>
                                <div className='flex h-8 w-fit items-center rounded border'>
                                  <button
                                    onClick={() => handleDecrementQty(item)}
                                    className='h-full px-3 '
                                  >
                                    <MinusIcon className='h-3 w-3 cursor-pointer' />
                                  </button>
                                  <span className='mx-2'>{formatNumber(displayedQuantity)}</span>
                                  <button
                                    onClick={() => handleIncremenQty(item, idx)}
                                    className={cn('h-full px-3', disable && 'cursor-not-allowed')}
                                    disabled={disable}
                                  >
                                    <PlusIcon className='h-3 w-3 cursor-pointer' />
                                  </button>
                                </div>
                                <button
                                  onClick={() => removeItem(item)}
                                  disabled={isLoading[item.id]}
                                >
                                  <Trash2 className='h-3 w-3' />
                                </button>
                                {multiOrigin && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                                      <CircleAlert className='h-3 w-3' />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align='center'
                                      className='z-[1000] w-[200px]'
                                    >
                                      <DropdownMenuLabel>
                                        See Where It’s Available
                                      </DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                        {suggestionStock(idx).map((stockItem, index) => (
                                          <DropdownMenuItem
                                            key={index}
                                            className='flex justify-between gap-x-3'
                                          >
                                            <span>{stockItem.location_name}</span>
                                            <span>{stockItem.quantity}</span>
                                          </DropdownMenuItem>
                                        ))}
                                      </DropdownMenuGroup>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className={cn(
                            ' col-span-2 text-right text-sm',
                            isOutOfStock ? 'text-gray-500' : 'theme_cart_drawer_color'
                          )}
                        >
                          {formatIDR(totalPrice)}
                        </div>
                      </div>
                      {multiOrigin && (
                        <div className='w-full'>
                          {multiOrigin &&
                            ((stock === 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                              <Badge variant='destructive' className='text-[10px]'>
                                {stock === 0 && isOutOfStock
                                  ? 'Stock Not Available'
                                  : 'Stock Is Not Enough'}
                              </Badge>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
            <DrawerFooter className='border-t px-0 pt-0'>
              <div className='theme_cart_drawer_color mb-4 px-4 pt-4'>
                <div className='mb-2 flex items-center justify-between text-base'>
                  <div>Estimated total</div>
                  <div>{formatIDR(subtotal || 0)}</div>
                </div>
                <p>Taxes, discounts and shipping calculated at checkout.</p>
              </div>
              <div className='flex w-full flex-col gap-3 px-4'>
                <Button
                  type='button'
                  variant='link'
                  className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
                  onClick={() => {
                    setOpenModal?.(false);
                    router.push(`/${token ? `?theme_id=${themeId}&token=${token}` : ''}`);
                  }}
                  disabled={isLoading['checkout']}
                >
                  {isLoading['checkout'] ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    theme?.cart?.labelContinueShopping || 'Continue shopping'
                  )}
                </Button>
                <Button
                  type='button'
                  variant='link'
                  className='theme_button theme_cart_drawer_btn flex w-full items-center px-24'
                  onClick={handleToCart}
                  disabled={
                    isLoading['checkout'] ||
                    pathname.startsWith('/checkout') ||
                    !!token ||
                    disableButton
                  }
                >
                  {isLoading['checkout'] ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <span className='mx-2'>{theme?.cart?.labelCheckout || 'Checkout'}</span>
                  )}
                </Button>
              </div>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}

export function CartPopup({
  openModal,
  setOpenModal,
  cart,
  host,
}: {
  openModal?: boolean;
  cart: BaseCart | null;
  setOpenModal?: (open: boolean) => void;
  host: string;
}) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const { data: carts } = useQuery({
    queryKey: ['carts'],
    queryFn: async () => {
      const carts = await retrieveCart(host as string);
      return carts?.data;
    },
  });
  const [currentItemQty] = React.useState<{ [key: string]: number }>({});
  const [subtotal, setSubtotal] = React.useState<number>(0);

  const cartItems = carts?.items;

  React.useEffect(() => {
    const newSubtotal = cartItems?.reduce((total, item) => {
      const qty = currentItemQty[item.id] || item.quantity;
      return total + (item.discount_price ? item.discount_price : item.sell_price) * qty;
    }, 0);
    setSubtotal(newSubtotal || 0);
  }, [currentItemQty, cartItems]);

  const images = cart?.product_assets[0];
  const displayedQuantity = cart?.quantity || 0;
  const totalPrice = displayedQuantity * (cart?.sell_price || 0);

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='top'>
      <DrawerContent className='theme_cart_drawer_bg theme_font fixed inset-x-0 top-0 z-50 flex h-auto w-full flex-col transition-all duration-1000'>
        {cart ? (
          <div className='theme_cart_drawer_color mx-2 px-4 pb-6 pt-2'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-inherit'>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Baru Saja Ditambahkan
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Barang
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Jumlah
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Total Harga
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className='hover:bg-inherit'>
                  <TableCell>
                    <div className='w-44'>
                      <Image
                        src={typeof images === 'object' ? images?.assets_url : images || ''}
                        width={100}
                        height={100}
                        className='h-auto w-full object-cover'
                        alt={cart.product_name}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{cart.product_name}</TableCell>
                  <TableCell>{cart.quantity}</TableCell>
                  <TableCell>{formatIDR(totalPrice || 0)}</TableCell>
                  <TableCell>
                    <div className='flex flex-col items-end text-end'>
                      <p>{formatIDR(subtotal)}</p>
                      <span className='text-xs font-semibold italic'>
                        *Belum Termasuk Biaya Pengiriman{' '}
                      </span>
                      <div className='flex gap-1 '>
                        <DrawerClose>
                          <Button type='button'>Continue Shopping</Button>
                        </DrawerClose>
                        <Button type='button' variant='outline'>
                          <Link href={`/${token ? `?theme_id=${themeId}&token=${token}` : ''}cart`}>
                            Cart
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

export function CartDropdownButton({
  preview,
  cart,
  host,
  theme,
  content,
}: {
  preview?: boolean;
  cart: HttpTypes.StoreCart;
  host: string;
  theme?: ThemeType;
  content?: HeaderElementDataType;
}) {
  const cartDrawer = useModal();
  const cartItems = cart?.items;
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';

  const totalItems =
    cartItems?.reduce((acc, item) => {
      return acc + item.quantity;
    }, 0) || 0;

  const backgroundColor = content?.colorSchemeHeader?.background || '#ffffff';
  const isDark = React.useMemo(() => isDarkColor(backgroundColor), [backgroundColor]);

  return (
    theme && (
      <>
        <div className='theme_cart_page_button'>
          <Link href={`/cart${preview ? `?isPreview=true&theme_id=${themeId as string}` : ''}`}>
            <div className='relative'>
              <ShoppingCartIcon className='h-7 w-7' />
              {cartItems && cartItems?.length > 0 && (
                <div
                  className={cn(
                    'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                    isDark ? 'bg-white text-black' : 'bg-black text-white'
                  )}
                >
                  {totalItems > 99 ? '99+' : totalItems}
                </div>
              )}
            </div>
          </Link>
        </div>
        <button onClick={() => cartDrawer.open()} className='theme_cart_drawer_button'>
          <div className='relative'>
            <ShoppingCartIcon className='h-7 w-7' />
            {cartItems && cartItems.length > 0 && (
              <div
                className={cn(
                  'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                  isDark ? 'bg-white text-black' : 'bg-black text-white'
                )}
              >
                {totalItems > 99 ? '99+' : totalItems}
              </div>
            )}
          </div>
        </button>
        <CartDrawer
          openModal={cartDrawer.isShow}
          setOpenModal={cartDrawer.setIsShow}
          cart={cart}
          host={host}
          theme={theme}
        />
      </>
    )
  );
}

function isDarkColor(hex: string): boolean {
  if (!hex) return false;

  const rgb = hex.match(/\w\w/g)?.map((x) => parseInt(x, 16));
  if (!rgb) return false;

  const [r, g, b] = rgb;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance < 128;
}
